import type { DatadogClient } from "@/clients/datadog.client";
import type { FpixelClient } from "@/clients/fpixel.client";
import type { GtagClient } from "@/clients/gtag.client";
import type { FirebaseClient } from "@/clients/firebase.client";
import type { ActionError } from "@/types";

type EmptyEventData = Record<never, never>;

type InteractionEventData = {
  accepted?: boolean;
  attempt?: number;
  billingPeriod?: string;
  branch?: string;
  campaignKeys?: string[];
  destination?: string;
  dictationUnused?: boolean;
  didPurchase?: boolean;
  display?: string;
  elapsedMs?: number;
  sizeMb?: number;
  network?: string;
  feature?: string;
  featureKey?: string;
  features?: string[];
  format?: string;
  fromStep?: string;
  granted?: boolean;
  hasPackage?: boolean;
  hasPackagesAtMount?: boolean;
  hours?: number;
  identifier?: string;
  identifiers?: string[];
  intent?: string | null;
  itemCount?: number;
  language?: string;
  lastStep?: string;
  lastStepIndex?: number;
  method?: string;
  package?: string;
  packageCount?: number;
  placement?: string;
  percent?: number | null;
  permission?: string;
  persona?: string | null;
  plan?: string;
  platform?: string;
  rating?: "good" | "bad";
  reason?: string;
  reasons?: string[];
  resultsCount?: number;
  source?: string;
  settingsFlavor?: string;
  status?: string;
  step?: string | number;
  count?: number;
  wordCount?: number;
  stepIndex?: number;
  stepKey?: string;
  surface?: string;
  transcriptId?: string;
  transcriptionProvider?: string;
  type?: string;
  unusedFeatures?: string[];
  useCases?: string[];
  useCasesCount?: number;
  utmSource?: string;
};

export type TelemetryEventData = {
  "session.started": EmptyEventData;
  "session.ended": EmptyEventData;
  "session.expired": { reason: "unauthorized" };
  "user.loaded": { userId: string };
  "user.updated": { userId: string };
  "transcript.created": { transcriptId: string; type?: string };
  "transcript.loaded": { transcriptId: string };
  "transcript.updated": { transcriptId: string };
  "transcript.deleted": { transcriptId: string };
  "transcript.restored": { transcriptId: string };
  "transcript.processingStarted": {
    transcriptId: string;
    operation: "fullAudio" | "speakerLabels";
  };
  "aiContent.requested": {
    transcriptId: string;
    feature: "translation" | "summary" | "flashcard" | "quiz";
  };
  "aiContent.completed": {
    transcriptId: string;
    feature: "translation" | "summary" | "flashcard" | "quiz";
  };
  "subscription.statusChanged": { isSubscribed: boolean };
  "subscription.checkoutStarted": { plan: string };
  "subscription.checkoutCompleted": {
    plan?: string;
    currency: string;
    value: number;
    transactionId: string;
  };
  "app.opened": { source: "launch" | "foreground"; firstLaunch: boolean };
  "locale.loadFailed": {
    stage: "preference" | "deviceLanguage" | "localeModule";
    reason: "invalid" | "loadFailed" | "timeout" | "unavailable";
    language?: string;
  };
  "navigation.pageViewed": { path: string };
  "poll.started": { transcriptId: string };
  "poll.completed": { transcriptId: string };
  "poll.failed": { transcriptId: string; error?: ActionError };
  "poll.cancelled": { transcriptId: string; error?: ActionError };
  "recording.statusChanged": {
    status: string;
    healthStatus?: string;
    pendingChunks?: number;
    pendingBytes?: number;
    isLocalBackupReady?: boolean;
    hasDataLoss?: boolean;
    droppedChunks?: number;
    failedChunks?: number;
    isSilenced?: boolean;
    pausedForSilence?: boolean;
    notificationPermissionGranted?: boolean;
    silenceAlertPosted?: boolean;
  };
  "runtime.desktopBridgeReady": {
    contractVersion: number;
    capabilityCount: number;
  };
  "runtime.desktopHostEvent": {
    status: string;
    reason?: string;
    updaterStage?: "check" | "download" | "staging";
    // Closed classification of the native updater error; the shell never ships
    // the message itself because it embeds install paths and account names.
    updaterCode?:
      | "signature"
      | "permission"
      | "disk-space"
      | "checksum"
      | "manifest"
      | "network"
      | "unknown";
  };
  "runtime.desktopUpdate": {
    status: string;
  };
  "feature.adopted": InteractionEventData;
  "auth.completed": InteractionEventData;
  "auth.handoffStarted": InteractionEventData;
  "auth.handoffCompleted": InteractionEventData;
  "auth.handoffFailed": InteractionEventData;
  "home.importSelected": InteractionEventData;
  "search.completed": InteractionEventData;
  "search.resultSelected": InteractionEventData;
  "featureDiscovery.selected": InteractionEventData;
  "transcript.copied": InteractionEventData;
  "transcript.exportSelected": InteractionEventData;
  // Carries `percent` so the funnel can tell whether the notice converts
  // differently on a barely-transcribed file than on a nearly-complete one.
  "transcript.partialProgressSelected": InteractionEventData;
  "transcript.previewViewed": InteractionEventData;
  "transcript.offerViewed": InteractionEventData;
  // The "Free draft" notice above a free-model transcript; display is
  // "partial" (preview of a longer file) or "full" (whole short file).
  "transcript.draftNoticeSelected": InteractionEventData;
  // The accuracy offer shown after a thumbs-down on a free draft.
  "transcript.ratingOfferSelected": InteractionEventData;
  "import.viewed": InteractionEventData;
  "import.pickerOpened": InteractionEventData;
  "import.filesSelected": InteractionEventData;
  "import.pickerCancelled": InteractionEventData;
  "import.uploadFailed": InteractionEventData;
  "import.uploadCompleted": InteractionEventData;
  "import.transcriptionCompleted": InteractionEventData;
  "import.transcriptShown": InteractionEventData;
  "translation.copied": InteractionEventData;
  "translation.exportSelected": InteractionEventData;
  "summary.copied": InteractionEventData;
  "paywall.viewed": InteractionEventData;
  "paywall.packagesLoaded": InteractionEventData;
  "paywall.planSelected": InteractionEventData;
  "paywall.purchaseStarted": InteractionEventData;
  "paywall.purchaseFinished": InteractionEventData;
  "paywall.purchaseCompleted": InteractionEventData;
  "paywall.skipped": InteractionEventData;
  "transcript.ratingSubmitted": InteractionEventData;
  "subscription.upgradeStarted": InteractionEventData;
  "subscription.cancelRequested": InteractionEventData;
  "subscription.cancelOfferShown": InteractionEventData;
  "subscription.cancelFeatureSelected": InteractionEventData;
  "subscription.cancelConfirmed": InteractionEventData;
  // Website-to-app transcript handoff and the first canonical transcript
  // experience before the visitor attaches an account.
  "guest.handoffStarted": InteractionEventData;
  "guest.handoffCompleted": InteractionEventData;
  "guest.handoffFailed": InteractionEventData;
  "guest.transcriptViewed": InteractionEventData;
  "guest.previewCopied": InteractionEventData;
  "guest.previewExported": InteractionEventData;
  "guest.audioPlayed": InteractionEventData;
  "guest.offerViewed": InteractionEventData;
  "guest.checkoutSelected": InteractionEventData;
  "settings.signInSelected": InteractionEventData;
  "settings.shareSelected": InteractionEventData;
  "acquisition.contextApplied": InteractionEventData;
  "acquisition.downloadSelected": InteractionEventData;
  "acquisition.downloadStarted": InteractionEventData;
  "onboarding.personaSelected": InteractionEventData;
  "onboarding.intentSelected": InteractionEventData;
  "onboarding.stepViewed": InteractionEventData;
  "onboarding.ahaDemoViewed": InteractionEventData;
  "onboarding.abandoned": InteractionEventData;
  "onboarding.reviewPromptViewed": InteractionEventData;
  "onboarding.reviewChoice": InteractionEventData;
  "onboarding.reviewRequestFinished": InteractionEventData;
  "dictation.onboardingOpened": InteractionEventData;
  "dictation.onboardingStepViewed": InteractionEventData;
  "dictation.onboardingCompleted": InteractionEventData;
  "dictation.onboardingSkipped": InteractionEventData;
  "dictation.permissionResult": InteractionEventData;
  "dictation.settingsOpened": InteractionEventData;
  "dictation.settingsReturned": InteractionEventData;
  "dictation.accessibilityDisclosureViewed": InteractionEventData;
  "dictation.accessibilityDisclosureDeclined": InteractionEventData;
  "dictation.accessibilityDisclosureAccepted": InteractionEventData;
  "dictation.desktopCrossSellSelected": InteractionEventData;
  "dictation.paywallRequired": InteractionEventData;
  "dictation.paywallDeferred": InteractionEventData;
  "dictation.flowSessionShown": InteractionEventData;
  "dictation.flowSessionDisabled": InteractionEventData;
  "dictation.web.start": {
    source:
      | "desktopBubble"
      | "desktopBubbleDictation"
      | "desktopShortcut"
      | "retry";
  };
  "dictation.web.finish": { latencyMs: number; audioBytes: number };
  "dictation.web.copied": {
    source: "automatic" | "desktopResult" | "history";
  };
  "dictation.web.error": { reason: string };
  "dictation.web.paywallRequired": EmptyEventData;
  "dictation.desktopInsertion": {
    status: "inserted" | "dispatched" | "not-inserted" | "failed";
    reason?: string;
  };
  "dictation.shortcutHint": { action: "shown" };
  "dictation.desktopRecording": {
    action:
      | "spoolStarted"
      | "promoted"
      | "promotionFailed"
      | "saved"
      | "recovered"
      | "deliveryFailed";
    durationMs?: number;
    audioBytes?: number;
  };
  "activationJourney.stepViewed": InteractionEventData;
  "activationJourney.stepSelected": InteractionEventData;
  "activationJourney.stepCompleted": InteractionEventData;
  "activationJourney.completed": InteractionEventData;
};

export type TelemetryEvent = keyof TelemetryEventData;

type CoreTelemetryEvent =
  | "session.started"
  | "session.ended"
  | "session.expired"
  | "user.loaded"
  | "user.updated"
  | "transcript.created"
  | "transcript.loaded"
  | "transcript.updated"
  | "transcript.deleted"
  | "transcript.restored"
  | "transcript.processingStarted"
  | "aiContent.requested"
  | "aiContent.completed"
  | "subscription.statusChanged"
  | "subscription.checkoutStarted"
  | "subscription.checkoutCompleted"
  | "app.opened"
  | "navigation.pageViewed"
  | "poll.started"
  | "poll.completed"
  | "poll.failed"
  | "poll.cancelled"
  | "recording.statusChanged";

export type ClientInteractionEvent = Exclude<
  TelemetryEvent,
  CoreTelemetryEvent
>;

export type TelemetryContext = {
  platform: "web" | "ios" | "android" | "desktop";
  isSubscribed: boolean;
  desktopOs?: "macos" | "windows";
  desktopVersion?: string;
  // Server-detected ISO country from remote config — powers geo breakdowns
  // of product analytics without any extra client geolocation.
  country?: string;
  // The interface language actually shown (appLang, stored choice, or device).
  appLanguage?: string;
  onboardingPersona?: string;
  onboardingIntent?: string;
  onboardingUseCases: string[];
};

export type TelemetryIdentity = { userId: string };

const _interactionFields = [
  "accepted",
  "attempt",
  "billingPeriod",
  "branch",
  "campaignKeys",
  "destination",
  "dictationUnused",
  "didPurchase",
  "display",
  "elapsedMs",
  "sizeMb",
  "network",
  "feature",
  "featureKey",
  "features",
  "format",
  "fromStep",
  "granted",
  "hasPackage",
  "hasPackagesAtMount",
  "hours",
  "identifier",
  "identifiers",
  "intent",
  "itemCount",
  "language",
  "lastStep",
  "lastStepIndex",
  "method",
  "package",
  "packageCount",
  "placement",
  "percent",
  "permission",
  "persona",
  "plan",
  "platform",
  "rating",
  "reason",
  "reasons",
  "resultsCount",
  "source",
  "settingsFlavor",
  "status",
  "step",
  "count",
  "wordCount",
  "stepIndex",
  "stepKey",
  "surface",
  "transcriptId",
  "transcriptionProvider",
  "type",
  "unusedFeatures",
  "useCases",
  "useCasesCount",
  "utmSource",
] as const;

const _attributionFields = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gad_source",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "entry",
  "angle",
  "channel",
] as const;

const _eventFields = {
  "session.started": [],
  "session.ended": [],
  "session.expired": ["reason"],
  "user.loaded": ["userId"],
  "user.updated": ["userId"],
  "transcript.created": ["transcriptId", "type"],
  "transcript.loaded": ["transcriptId"],
  "transcript.updated": ["transcriptId"],
  "transcript.deleted": ["transcriptId"],
  "transcript.restored": ["transcriptId"],
  "transcript.processingStarted": ["transcriptId", "operation"],
  "aiContent.requested": ["transcriptId", "feature"],
  "aiContent.completed": ["transcriptId", "feature"],
  "subscription.statusChanged": ["isSubscribed"],
  "subscription.checkoutStarted": ["plan"],
  "subscription.checkoutCompleted": [
    "plan",
    "currency",
    "value",
    "transactionId",
  ],
  "app.opened": ["source", "firstLaunch"],
  "locale.loadFailed": ["stage", "reason", "language"],
  "navigation.pageViewed": ["path"],
  "poll.started": ["transcriptId"],
  "poll.completed": ["transcriptId"],
  "poll.failed": ["transcriptId", "error"],
  "poll.cancelled": ["transcriptId", "error"],
  "recording.statusChanged": [
    "status",
    "healthStatus",
    "pendingChunks",
    "pendingBytes",
    "isLocalBackupReady",
    "hasDataLoss",
    "droppedChunks",
    "failedChunks",
    "isSilenced",
    "pausedForSilence",
    "notificationPermissionGranted",
    "silenceAlertPosted",
  ],
  "runtime.desktopBridgeReady": ["contractVersion", "capabilityCount"],
  "runtime.desktopHostEvent": [
    "status",
    "reason",
    "updaterStage",
    "updaterCode",
  ],
  "runtime.desktopUpdate": ["status"],
  "feature.adopted": _interactionFields,
  "auth.completed": _interactionFields,
  "auth.handoffStarted": _interactionFields,
  "auth.handoffCompleted": _interactionFields,
  "auth.handoffFailed": _interactionFields,
  "home.importSelected": _interactionFields,
  "search.completed": _interactionFields,
  "search.resultSelected": _interactionFields,
  "featureDiscovery.selected": _interactionFields,
  "transcript.copied": _interactionFields,
  "transcript.exportSelected": _interactionFields,
  "transcript.partialProgressSelected": _interactionFields,
  "transcript.previewViewed": _interactionFields,
  "transcript.offerViewed": _interactionFields,
  "transcript.draftNoticeSelected": _interactionFields,
  "transcript.ratingOfferSelected": _interactionFields,
  "import.viewed": _interactionFields,
  "import.pickerOpened": _interactionFields,
  "import.filesSelected": _interactionFields,
  "import.pickerCancelled": _interactionFields,
  "import.uploadFailed": _interactionFields,
  "import.uploadCompleted": _interactionFields,
  "import.transcriptionCompleted": _interactionFields,
  "import.transcriptShown": _interactionFields,
  "translation.copied": _interactionFields,
  "translation.exportSelected": _interactionFields,
  "summary.copied": _interactionFields,
  "paywall.viewed": _interactionFields,
  "paywall.packagesLoaded": _interactionFields,
  "paywall.planSelected": _interactionFields,
  "paywall.purchaseStarted": _interactionFields,
  "paywall.purchaseFinished": _interactionFields,
  "paywall.purchaseCompleted": _interactionFields,
  "paywall.skipped": _interactionFields,
  "transcript.ratingSubmitted": _interactionFields,
  "subscription.upgradeStarted": _interactionFields,
  "subscription.cancelRequested": _interactionFields,
  "subscription.cancelOfferShown": _interactionFields,
  "subscription.cancelFeatureSelected": _interactionFields,
  "subscription.cancelConfirmed": _interactionFields,
  "guest.handoffStarted": _interactionFields,
  "guest.handoffCompleted": _interactionFields,
  "guest.handoffFailed": _interactionFields,
  "guest.transcriptViewed": _interactionFields,
  "guest.previewCopied": _interactionFields,
  "guest.previewExported": _interactionFields,
  "guest.audioPlayed": _interactionFields,
  "guest.offerViewed": _interactionFields,
  "guest.checkoutSelected": _interactionFields,
  "settings.signInSelected": _interactionFields,
  "settings.shareSelected": _interactionFields,
  "acquisition.contextApplied": _interactionFields,
  "acquisition.downloadSelected": _interactionFields,
  "acquisition.downloadStarted": _interactionFields,
  "onboarding.personaSelected": _interactionFields,
  "onboarding.intentSelected": _interactionFields,
  "onboarding.stepViewed": _interactionFields,
  "onboarding.ahaDemoViewed": _interactionFields,
  "onboarding.abandoned": _interactionFields,
  "onboarding.reviewPromptViewed": _interactionFields,
  "onboarding.reviewChoice": _interactionFields,
  "onboarding.reviewRequestFinished": _interactionFields,
  "dictation.onboardingOpened": _interactionFields,
  "dictation.onboardingStepViewed": _interactionFields,
  "dictation.onboardingCompleted": _interactionFields,
  "dictation.onboardingSkipped": _interactionFields,
  "dictation.permissionResult": _interactionFields,
  "dictation.settingsOpened": _interactionFields,
  "dictation.settingsReturned": _interactionFields,
  "dictation.accessibilityDisclosureViewed": _interactionFields,
  "dictation.accessibilityDisclosureDeclined": _interactionFields,
  "dictation.accessibilityDisclosureAccepted": _interactionFields,
  "dictation.desktopCrossSellSelected": _interactionFields,
  "dictation.paywallRequired": _interactionFields,
  "dictation.paywallDeferred": _interactionFields,
  "dictation.flowSessionShown": _interactionFields,
  "dictation.flowSessionDisabled": _interactionFields,
  "dictation.web.start": ["source"],
  "dictation.web.finish": ["latencyMs", "audioBytes"],
  "dictation.web.copied": ["source"],
  "dictation.web.error": ["reason"],
  "dictation.web.paywallRequired": [],
  "dictation.desktopInsertion": ["status", "reason"],
  "dictation.shortcutHint": ["action"],
  "dictation.desktopRecording": ["action", "durationMs", "audioBytes"],
  "activationJourney.stepViewed": _interactionFields,
  "activationJourney.stepSelected": _interactionFields,
  "activationJourney.stepCompleted": _interactionFields,
  "activationJourney.completed": _interactionFields,
} as const satisfies Record<TelemetryEvent, readonly string[]>;

type TelemetryVendor = "gtag" | "fpixel";

const _marketingRoutes: Partial<
  Record<TelemetryEvent, readonly TelemetryVendor[]>
> = {
  "subscription.checkoutStarted": ["gtag", "fpixel"],
  "subscription.checkoutCompleted": ["gtag", "fpixel"],
  // A free user reading a finished transcript: the mid-funnel step closest to
  // a purchase, so Meta has more than checkouts to optimise ads against.
  "transcript.previewViewed": ["fpixel"],
};

const _contextFields = [
  "platform",
  "isSubscribed",
  "desktopOs",
  "desktopVersion",
  "country",
  "appLanguage",
  "onboardingPersona",
  "onboardingIntent",
  "onboardingUseCases",
] as const satisfies readonly (keyof TelemetryContext)[];

const _isSafePrimitive = (value: unknown): value is string | number | boolean =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

const _sanitize = (
  value: unknown,
  allowedFields: readonly string[],
): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const source = value as Record<string, unknown>;
  const sanitized: Record<string, unknown> = {};
  for (const field of allowedFields) {
    const candidate = source[field];
    if (_isSafePrimitive(candidate)) sanitized[field] = candidate;
    if (
      Array.isArray(candidate) &&
      candidate.every((item) => _isSafePrimitive(item))
    ) {
      sanitized[field] = [...candidate];
    }
  }
  return sanitized;
};

const _safeSource = (value: string): string =>
  /^[a-zA-Z0-9._:-]{1,120}$/.test(value) ? value : "unknown";

const _safeCrashCode = (value: unknown): string | undefined =>
  typeof value === "string" && /^[a-zA-Z][a-zA-Z0-9]{0,63}$/.test(value)
    ? value
    : undefined;

export type TelemetryDependencies = {
  datadog: DatadogClient;
  firebase?: FirebaseClient;
  getFirebaseProperties?: () => Record<string, string>;
  fpixel?: FpixelClient;
  gtag?: GtagClient;
  getContext: () => TelemetryContext;
  getAttribution?: () => Record<string, unknown>;
};

export const createTelemetry = (dependencies: TelemetryDependencies) => {
  let started = false;
  let currentUserId: string | undefined;
  let attribution: Record<string, unknown> = {};
  let firebaseIdentityTask: Promise<void> = Promise.resolve();

  const _queueFirebaseIdentity = (operation: () => Promise<void>): void => {
    firebaseIdentityTask = firebaseIdentityTask.then(operation).catch(() => {});
  };

  const _context = () => _sanitize(dependencies.getContext(), _contextFields);
  const _globalContext = () => ({ ..._context(), attribution });

  const _track = <Event extends TelemetryEvent>(
    event: Event,
    data?: TelemetryEventData[Event],
  ): void => {
    if (!started) return;
    const payload = _sanitize(data, _eventFields[event]);
    dependencies.datadog.info("client.event", {
      context: _context(),
      data: payload,
      event,
    });

    const routes: readonly TelemetryVendor[] = _marketingRoutes[event] || [];
    if (routes.includes("gtag")) {
      if (event === "subscription.checkoutStarted") {
        dependencies.gtag?.event("begin_checkout", payload);
      }
      if (event === "subscription.checkoutCompleted") {
        dependencies.gtag?.conversion({
          value: Number(payload.value),
          transactionId: String(payload.transactionId),
          currency: String(payload.currency),
        });
        dependencies.gtag?.event("purchase", {
          plan: payload.plan,
          currency: payload.currency,
          value: payload.value,
          transaction_id: payload.transactionId,
        });
      }
    }
    if (routes.includes("fpixel") && event === "transcript.previewViewed") {
      dependencies.fpixel?.event("ViewContent", {
        content_type: "transcript",
      });
    }
    if (routes.includes("fpixel") && event === "subscription.checkoutStarted") {
      dependencies.fpixel?.event("InitiateCheckout", {
        content_name: payload.plan,
      });
    }
    if (
      routes.includes("fpixel") &&
      event === "subscription.checkoutCompleted"
    ) {
      dependencies.fpixel?.event(
        "Subscribe",
        {
          currency: payload.currency,
          value: payload.value,
        },
        {
          eventID: String(payload.transactionId),
        },
      );
    }
  };

  return {
    start: () => {
      if (started) return;
      dependencies.datadog.start();
      started = true;
      attribution = _sanitize(
        dependencies.getAttribution?.(),
        _attributionFields,
      );
      dependencies.datadog.setContext(_globalContext());
    },

    track: _track,

    trackPurchase: (
      data: TelemetryEventData["subscription.checkoutCompleted"],
      marketing?: { email?: string },
    ) => {
      if (!started) return;
      if (marketing?.email) {
        dependencies.gtag?.userData(marketing.email.trim().toLowerCase());
      }
      _track("subscription.checkoutCompleted", data);
    },

    error: (source: string, data?: Record<string, unknown>) => {
      if (!started) return;
      const code = _safeCrashCode(data?.code);
      dependencies.datadog.error("client.error", {
        context: _context(),
        data: {
          ..._sanitize(data, [
            "component",
            "error",
            "name",
            "operation",
            "phase",
            "status",
            "statusCode",
          ]),
          ...(code ? { code } : {}),
        },
        event: "client.error",
        source: _safeSource(source),
      });
    },

    identify: (identity: TelemetryIdentity) => {
      if (!started || currentUserId === identity.userId) return;
      currentUserId = identity.userId;
      dependencies.datadog.identify(identity.userId);
      if (dependencies.firebase) {
        _queueFirebaseIdentity(async () => {
          await dependencies.firebase?.start();
          await dependencies.firebase?.identify(identity.userId);
          for (const [key, value] of Object.entries(
            dependencies.getFirebaseProperties?.() || {},
          )) {
            await dependencies.firebase?.setProperty(key, value);
          }
        });
      }
      dependencies.datadog.setContext(_globalContext());
    },

    clearIdentity: () => {
      if (!started || currentUserId === undefined) return;
      currentUserId = undefined;
      dependencies.datadog.clearIdentity();
      if (dependencies.firebase) {
        _queueFirebaseIdentity(() => dependencies.firebase!.clearIdentity());
      }
      dependencies.datadog.setContext(_globalContext());
    },

    refreshContext: () => {
      if (started) dependencies.datadog.setContext(_globalContext());
    },

    pageview: (path: string) => {
      if (!started) return;
      _track("navigation.pageViewed", { path });
      dependencies.gtag?.pageview(path);
      dependencies.fpixel?.pageview();
    },
  };
};

export type Telemetry = ReturnType<typeof createTelemetry>;

const _serializeCrashValue = (value: unknown): string | undefined => {
  if (value == null) return undefined;
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const _normalizeCrashError = (error: unknown) => {
  if (error instanceof Error) {
    return {
      code:
        error.name === "ClientOperationError"
          ? _safeCrashCode((error as Error & { code?: unknown }).code)
          : undefined,
      name: error.name,
    };
  }
  if (error && typeof error === "object") {
    const routeError = error as {
      code?: unknown;
      status?: number;
      name?: string;
    };
    return {
      code: _safeCrashCode(routeError.code),
      name: routeError.name || "RouteError",
      status: routeError.status,
    };
  }
  return {
    name: typeof error,
    kind: _serializeCrashValue(error) ? "value" : "empty",
  };
};

let _productionTelemetry: Pick<Telemetry, "error" | "track"> | undefined;

export const configureTelemetryFacade = (
  telemetry: Pick<Telemetry, "error" | "track">,
): void => {
  _productionTelemetry = telemetry;
};

export const logBrowserCrash = (
  source: string,
  error: unknown,
  extra?: {
    code?: ActionError;
    component?: string;
    operation?: string;
    phase?: string;
  },
): void => {
  const normalized = _normalizeCrashError(error);
  _productionTelemetry?.error(source, {
    ..._sanitize(extra, ["component", "operation", "phase"]),
    code: normalized.code || _safeCrashCode(extra?.code),
    name: normalized.name,
    status: normalized.status,
  });
};

export const logClientEvent = <Event extends ClientInteractionEvent>(
  event: Event,
  data?: TelemetryEventData[Event],
): void => {
  _productionTelemetry?.track(event, data);
};
